import { z } from 'zod';

export const MULTIMODAL_QUERY_MAX_BYTES = 20 * 1024 * 1024;
export const MULTIMODAL_QUERY_MAX_TEXT_CHARS = 4_000;

export const MULTIMODAL_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/avif',
  'image/bmp',
  'image/gif',
] as const;

export const MULTIMODAL_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'audio/m4a',
  'audio/mp4',
] as const;

export const MULTIMODAL_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/mpeg',
  'video/webm',
  'video/quicktime',
] as const;

export type MultimodalImageMime = typeof MULTIMODAL_IMAGE_MIME_TYPES[number];
export type MultimodalAudioMime = typeof MULTIMODAL_AUDIO_MIME_TYPES[number];
export type MultimodalVideoMime = typeof MULTIMODAL_VIDEO_MIME_TYPES[number];
export type MultimodalMime = MultimodalImageMime | MultimodalAudioMime | MultimodalVideoMime;

export type MultimodalModality = 'text' | 'image' | 'audio' | 'video';

export const multimodalQueryTextSchema = z
  .string()
  .trim()
  .min(1, 'Il testo della query non può essere vuoto')
  .max(MULTIMODAL_QUERY_MAX_TEXT_CHARS, `Il testo non può superare ${MULTIMODAL_QUERY_MAX_TEXT_CHARS} caratteri`);

export const multimodalQueryOptionsSchema = z.object({
  agentId: z.string().uuid().optional(),
  limit: z.number().int().min(1).max(50).optional(),
  rerankTopN: z.number().int().min(1).max(20).optional(),
  inferentialEnabled: z.boolean().optional(),
  language: z.string().min(2).max(20).optional(),
  skipCache: z.boolean().optional(),
});

export type MultimodalQueryOptions = z.infer<typeof multimodalQueryOptionsSchema>;

/**
 * Boundary schema for the multimodal RAG query payload (post multipart parsing).
 * Discriminated union over the modality of the optional attachment, ensuring
 * at least one of `text` / `image` / `audio` / `video` is provided.
 */
const attachmentInputSchema = z.object({
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative().max(MULTIMODAL_QUERY_MAX_BYTES),
});

export const multimodalQuerySchema = z
  .object({
    text: multimodalQueryTextSchema.optional(),
    image: attachmentInputSchema
      .extend({ mimeType: z.enum(MULTIMODAL_IMAGE_MIME_TYPES) })
      .optional(),
    audio: attachmentInputSchema
      .extend({ mimeType: z.enum(MULTIMODAL_AUDIO_MIME_TYPES) })
      .optional(),
    video: attachmentInputSchema
      .extend({ mimeType: z.enum(MULTIMODAL_VIDEO_MIME_TYPES) })
      .optional(),
    options: multimodalQueryOptionsSchema.optional(),
  })
  .refine(
    (v) => Boolean(v.text || v.image || v.audio || v.video),
    { message: "Almeno uno tra 'text', 'image', 'audio', 'video' è richiesto" },
  );

export type MultimodalQueryInput = z.infer<typeof multimodalQuerySchema>;

export interface MultimodalQueryAttachment {
  modality: Exclude<MultimodalModality, 'text'>;
  mimeType: MultimodalMime;
  sizeBytes: number;
  storageKey: string | null;
}

export interface MultimodalQueryPipeline {
  retrieval_chunks: number;
  rerank_chunks: number | null;
  inferential_status: 'OK' | 'SKIP' | 'DISABLED' | null;
  total_ms: number;
  retrieval_ms: number;
  inferential_ms?: number;
  embedding_ms: number;
  embedding_modality: MultimodalModality;
}

export interface MultimodalQueryResultItem {
  content: string;
  source_name: string;
  similarity: number;
  chunk_id?: string;
  source_id?: string | null;
  page_number?: number | null;
}

export interface MultimodalQueryResponse {
  found: boolean;
  query: string;
  modality: MultimodalModality;
  modalities: MultimodalModality[];
  attachments: MultimodalQueryAttachment[];
  results: MultimodalQueryResultItem[];
  synthesized_answer?: string;
  confidence_score?: number;
  sources?: string[];
  pipeline: MultimodalQueryPipeline;
  message: string;
}

/**
 * Contratto di richiesta lato boundary (post multipart parsing).
 * Discriminated union sulla presenza di almeno un input.
 */
export interface MultimodalQuery {
  text?: string;
  attachments: MultimodalQueryAttachment[];
  options?: MultimodalQueryOptions;
}

/** Alias semantico per il singolo risultato della query multimodale. */
export type MultimodalQueryResult = MultimodalQueryResponse;

export interface MultimodalQueryErrorBody {
  success: false;
  error: string;
  error_code:
    | 'multimodal_query_requires_gemini_embedder'
    | 'multimodal_query_no_input'
    | 'multimodal_query_payload_too_large'
    | 'multimodal_query_unsupported_mime'
    | 'multimodal_query_rate_limited'
    | 'multimodal_query_validation_failed'
    | 'multimodal_query_internal_error';
  details?: unknown;
  retry_after_seconds?: number;
}
